import type { BibleLocation } from '@skjv/common';
import { parseChapterId } from '@skjv/common';
import { parseVerseId } from './parse-verse-location';

// One indicator per bookmarked verse — the Bookmarks panel glyph (see
// components/icons/bookmark-icon.tsx) overlaid on the verse label, matching
// the mobile app's -bm badge. Favorite color when any bookmark at the verse
// is a favorite; a count inside the icon when there is more than one.
const BOOKMARK_GLYPH =
  '<svg viewBox="0 0 24 24" fill="currentColor" class="bookmark-glyph"><path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/></svg>';

/** Add bookmark indicators to verses in a chapter */
export function addBookmarkIndicators(
  chapterElement: HTMLElement,
  _chapterLocation: BibleLocation,
  getBookmarksAt?: (location: BibleLocation) => Array<{ name: string; isFavorite?: boolean }>,
) {
  if (!getBookmarksAt) return;

  chapterElement.querySelectorAll('.verse').forEach((verseEl) => {
    const id = verseEl.id;
    const location = parseVerseId(id);

    const bookmarks = getBookmarksAt(location);
    if (bookmarks.length === 0) return;

    // Check if indicator already exists on the verse element
    if (verseEl.querySelector('.bookmark-indicator')) return;

    const indicator = document.createElement('span');
    indicator.className = 'bookmark-indicator';
    if (bookmarks.some((b) => b.isFavorite)) indicator.classList.add('is-favorite');
    indicator.innerHTML = BOOKMARK_GLYPH;

    if (bookmarks.length > 1) {
      const count = document.createElement('span');
      count.className = 'bookmark-count';
      count.textContent = String(bookmarks.length);
      indicator.appendChild(count);
    }

    // Tooltip with every bookmark name at the verse
    const tooltip = document.createElement('span');
    tooltip.className = 'bookmark-tooltip';
    tooltip.textContent = bookmarks.map((b) => b.name).join(', ');
    indicator.appendChild(tooltip);

    // Anchor to the verse NUMBER — absolute positioning against the verse
    // block lands mid-text on wrapped verses and paints the icon over a
    // word (UAT 2026-08-28). The number always sits clear of the prose.
    (verseEl.querySelector('.verse-num') ?? verseEl).appendChild(indicator);
  });
}

/** Refresh bookmark indicators for all loaded chapters */
export function refreshBookmarkIndicators(
  container: HTMLElement,
  loadedChapters: Map<string, HTMLElement>,
  getBookmarksAt?: (location: BibleLocation) => Array<{ name: string; isFavorite?: boolean }>,
) {
  // Remove all existing indicators
  container.querySelectorAll('.bookmark-indicator').forEach((el) => el.remove());

  // Re-add indicators for all loaded chapters
  for (const [chapterId, chapterElement] of loadedChapters) {
    const location = parseChapterId(chapterId);
    if (location) {
      addBookmarkIndicators(chapterElement, location, getBookmarksAt);
    }
  }
}
